import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import i18n from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Clock, AlertTriangle, Save, Key, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from './ui/SignaturePad';
import { useDefaultSignature } from '../hooks/useDefaultSignature';
import FileUpload from './FileUpload';
import AttachmentManager from './AttachmentManager';
import useSubTabShortcut from '../hooks/useSubTabShortcut';
import { 
  FormDatePicker, 
  FormInput, 
  FormSelect,
  RestrictedInput, 
  RestrictedSelect, 
  CertificateSection,
  EmergencyContactSection,
  DATE_FIELDS,
  RESTRICTED_FIELDS,
  isRestricted,
  convertFormDates
} from './shared/EmployeeFormFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyProfile = ({ isAdmin = false }) => {
  const { t } = useTranslation();
  const [employee, setEmployee] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  useSubTabShortcut({
    tab: 'my-profile', ctxKey: 'profileSubTab', value: activeTab, setValue: setActiveTab,
    labels: { details: 'auto.proj.employeeDetails', avatar: 'auto.proj.avatar', attachments: 'auto.proj.attachments',
      signature: 'auto.proj.signature', security: 'auto.proj.security' },
  });
  const [detailsSubTab, setDetailsSubTab] = useState('basic');
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Fetch employee profile
  const fetchMyProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me/employee`);
      if (response.data.linked && response.data.employee) {
        const emp = response.data.employee;
        setEmployee(emp);
        
        // Initialize form with employee data
        const formData = { ...emp };
        // Convert date strings to Date objects
        DATE_FIELDS.forEach(field => {
          if (formData[field]) {
            formData[field] = new Date(formData[field]);
          }
        });
        setEditForm(formData);
        setOriginalForm(JSON.parse(JSON.stringify(formData)));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error(t('auto.proj.failedToLoadYourProfile'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchPendingChanges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profile-changes`);
      setPendingChanges(response.data.filter(r => r.status === 'pending'));
    } catch (error) {
      console.error('Error fetching pending changes:', error);
    }
  }, []);

  useEffect(() => {
    fetchMyProfile();
    fetchSettings();
    fetchPendingChanges();
  }, [fetchMyProfile, fetchSettings, fetchPendingChanges]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(editForm) !== JSON.stringify(originalForm);
    setHasUnsavedChanges(hasChanges);
  }, [editForm, originalForm]);

  const handleInputChange = (field, value) => {
    if (!isRestricted(field)) {
      setEditForm(prev => ({ ...prev, [field]: value  }));
    }
  };

  const handleDateChange = (field, date) => {
    if (!isRestricted(field)) {
      setEditForm(prev => ({ ...prev, [field]: date  }));
    }
  };

  const handleEmergencyContactChange = (field, value) => {
    setEditForm({
      ...editForm,
      emergency_contact: {
        ...(editForm.emergency_contact || {}),
        [field]: value
      }
    });
  };

  const handleCertificateToggle = (category, certType, checked) => {
    const currentCerts = editForm[category] || [];
    
    if (checked) {
      setEditForm({
        ...editForm,
        [category]: [...currentCerts, { type: certType, expiry_date: null }]
      });
    } else {
      setEditForm({
        ...editForm,
        [category]: currentCerts.filter(cert => cert.type !== certType)
      });
    }
  };

  const handleCertificateExpiryChange = (category, certType, date) => {
    const currentCerts = editForm[category] || [];
    const updatedCerts = currentCerts.map(cert =>
      cert.type === certType ? { ...cert, expiry_date: date } : cert
    );
    
    setEditForm({
      ...editForm,
      [category]: updatedCerts
    });
  };

  const isCertificateChecked = useCallback((category, certType) => {
    const certs = editForm[category] || [];
    return certs.some(cert => cert.type === certType);
  }, [editForm]);

  const getCertificateExpiryDate = useCallback((category, certType) => {
    const certs = editForm[category] || [];
    const cert = certs.find(c => c.type === certType);
    return cert?.expiry_date || null;
  }, [editForm]);

  const handleSubmitChanges = async () => {
    // Find which fields have changed (excluding restricted fields)
    const changes = {};
    
    const compareValues = (key, newVal, oldVal) => {
      if (RESTRICTED_FIELDS.has(key)) return false;
      
      if (newVal instanceof Date && oldVal instanceof Date) {
        return newVal.getTime() !== oldVal.getTime();
      }
      if (newVal instanceof Date || oldVal instanceof Date) {
        return true;
      }
      if (typeof newVal === 'object' && typeof oldVal === 'object') {
        return JSON.stringify(newVal) !== JSON.stringify(oldVal);
      }
      return newVal !== oldVal;
    };

    Object.keys(editForm).forEach(key => {
      if (compareValues(key, editForm[key], originalForm[key])) {
        let value = editForm[key];
        if (value instanceof Date) {
          value = value.toISOString();
        }
        changes[key] = value;
      }
    });

    if (Object.keys(changes).length === 0) {
      toast.info(t('auto.proj.noChangesDetected'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/profile-changes`, { changes });
      toast.success(t('auto.proj.profileChangeRequestSubmittedForApproval'));
      setOriginalForm(JSON.parse(JSON.stringify(editForm)));
      setHasUnsavedChanges(false);
      fetchPendingChanges();
    } catch (error) {
      console.error('Error submitting changes:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit changes');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAvatarUploadComplete = (filename) => {
    setEmployee(prev => ({ ...prev, avatar_file: filename  }));
    toast.success(t('auto.proj.avatarUploadedSuccessfully'));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error(t('auto.proj.newPasswordsDoNotMatch'));
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(t('auto.proj.newPasswordMustBeAtLeast'));
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success(t('auto.proj.passwordChangedSuccessfully'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      console.error('Password change error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">{t('auto.proj.loadingYourProfile')}</div>;
  }

  if (!employee) {
    return (
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">{t('auto.proj.accountNotLinked')}</p>
              <p className="text-amber-700">
                {t('auto.proj.yourAccountIsNotLinkedTo')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            {employee.avatar_file ? (
              <AvatarImage src={`${BACKEND_URL}/api/employees/avatar/${employee.avatar_file}`} />
            ) : null}
            <AvatarFallback className="text-xl bg-slate-200">
              {(employee.name || employee.first_name || '?')[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Employee'}
            </h2>
            <p className="text-slate-600">{employee.department || 'No department'} • {employee.position_1 || 'No position'}</p>
            <Badge className={employee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {employee.status || 'Active'}
            </Badge>
          </div>
        </div>
        
        {hasUnsavedChanges && (
          <Button onClick={handleSubmitChanges} disabled={submitting} className="flex items-center gap-2" data-testid="btn-submitchanges">
            <Save className="w-4 h-4" />
            {submitting ? t('auto.proj.submitting') : t('auto.proj.submitChangesForApproval')}
          </Button>
        )}
      </div>

      {/* Pending Changes Banner */}
      {pendingChanges.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">{t('auto.proj.pendingProfileChanges')}</p>
                <p className="text-sm text-blue-600">
                  You have {pendingChanges.length} profile change request(s) awaiting admin approval.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unsaved Changes Banner */}
      {hasUnsavedChanges && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">{t('auto.proj.unsavedChanges')}</p>
                <p className="text-sm text-amber-600">
                  You have unsaved changes. Click &quot;Submit Changes for Approval&quot; to send them for admin review.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="details" className="px-3 py-2" data-testid="tab-details">{t('auto.proj.employeeDetails')}</TabsTrigger>
          <TabsTrigger value="avatar" className="px-3 py-2" data-testid="tab-avatar">{t('auto.proj.avatar')}</TabsTrigger>
          <TabsTrigger value="attachments" className="px-3 py-2" data-testid="tab-attachments">{t('auto.proj.attachments')}</TabsTrigger>
          <TabsTrigger value="signature" className="px-3 py-2" data-testid="tab-signature">{t('auto.proj.signature')}</TabsTrigger>
          <TabsTrigger value="security" className="px-3 py-2 flex items-center gap-1" data-testid="tab-security">
            <Lock className="w-4 h-4" />
            {t('auto.proj.security')}
          </TabsTrigger>
        </TabsList>

        {/* Signature Tab — default handwritten signature reused
            across all sign-off screens via useDefaultSignature */}
        <TabsContent value="signature" className="space-y-4">
          <MyDefaultSignatureCard />
          <MyLanguageCard />
        </TabsContent>

        {/* Employee Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <Tabs value={detailsSubTab} onValueChange={setDetailsSubTab} className="space-y-4">
            <TabsList className="flex flex-wrap h-auto gap-1 p-1 bg-slate-100">
              <TabsTrigger value="basic" className="px-3 py-2" data-testid="tab-basic">{t('auto.proj.basicInfo')}</TabsTrigger>
              <TabsTrigger value="personal" className="px-3 py-2" data-testid="tab-personal">{t('auto.proj.personal')}</TabsTrigger>
              <TabsTrigger value="business" className="px-3 py-2" data-testid="tab-business">{t('auto.proj.business')}</TabsTrigger>
              <TabsTrigger value="certificates" className="px-3 py-2" data-testid="tab-certificates">{t('auto.proj.certificates')}</TabsTrigger>
              <TabsTrigger value="visa" className="px-3 py-2" data-testid="tab-visa">Visa &amp; Permits</TabsTrigger>
            </TabsList>

            {/* Basic Info Sub-Tab */}
            <TabsContent value="basic" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('auto.proj.basicInformation')}</CardTitle>
                  <CardDescription>
                    Fields marked with <span className="text-amber-600">{t('auto.proj.adminOnly')}</span> cannot be changed by employees.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('auto.proj.fullName2')}</Label>
                      <Input
                        value={editForm.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                      />
                    </div>
                    
                    <RestrictedSelect 
                      field="status" 
                      label={t('auto.proj.status')} 
                      value={editForm.status || 'active'}
                      options={['active', 'not_active']}
                      placeholder={t('auto.proj.selectStatus')}
                    />

                    <RestrictedInput field="email" label={t('auto.proj.email')} type="email" value={editForm.email} />

                    <div>
                      <Label>{t('auto.proj.phone')}</Label>
                      <Input value={editForm.phone || ''} onChange={(e) => handleInputChange('phone', e.target.value)} />
                    </div>

                    <RestrictedSelect 
                      field="department" 
                      label={t('auto.proj.department')} 
                      value={editForm.department}
                      options={settings?.departments}
                      placeholder={t('auto.proj.selectDepartment')}
                    />

                    <RestrictedSelect 
                      field="position_1" 
                      label={t('auto.proj.position1')} 
                      value={editForm.position_1}
                      options={settings?.positions}
                      placeholder={t('auto.proj.selectPosition')}
                    />

                    <RestrictedSelect 
                      field="position_2" 
                      label={t('auto.proj.position2Optional')} 
                      value={editForm.position_2 || 'none'}
                      options={['none', ...(settings?.positions || [])]}
                      placeholder={t('auto.proj.selectPosition')}
                    />

                    <RestrictedSelect 
                      field="position_3" 
                      label={t('auto.proj.position3Optional')} 
                      value={editForm.position_3 || 'none'}
                      options={['none', ...(settings?.positions || [])]}
                      placeholder={t('auto.proj.selectPosition')}
                    />

                    <div>
                      <Label>{t('auto.proj.address')}</Label>
                      <Input value={editForm.address || ''} onChange={(e) => handleInputChange('address', e.target.value)} />
                    </div>

                    <div>
                      <Label>{t('auto.proj.country')}</Label>
                      <Select value={editForm.country || ''} onValueChange={(value) => handleInputChange('country', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectCountry')} /></SelectTrigger>
                        <SelectContent>
                          {settings?.countries?.map(country => (<SelectItem key={country} value={country}>{country}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <FormDatePicker field="start_date" label={t('auto.proj.startDate2')} value={editForm.start_date} disabled={true} onDateChange={handleDateChange} />
                    <FormDatePicker field="hire_date" label={t('auto.proj.hireDate')} value={editForm.hire_date} disabled={true} onDateChange={handleDateChange} />
                  </div>
                </CardContent>
              </Card>

              {/* Hourly Rates - All read-only for employees */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('auto.proj.hourlyRates')}</CardTitle>
                  <CardDescription>
                    <span className="text-amber-600">{t('auto.proj.hourlyRatesAreSetByAdministrators')}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <RestrictedInput field="hourly_rate_1" label={t('auto.proj.hourlyRate1')} type="number" value={editForm.hourly_rate_1} />
                    <RestrictedSelect 
                      field="currency_1" 
                      label={t('auto.proj.currency1')} 
                      value={editForm.currency_1}
                      options={settings?.currencies}
                      placeholder={t('auto.proj.selectCurrency2')}
                    />
                    <div />

                    <RestrictedInput field="hourly_rate_2" label={t('auto.proj.hourlyRate2')} type="number" value={editForm.hourly_rate_2} />
                    <RestrictedSelect 
                      field="currency_2" 
                      label={t('auto.proj.currency2')} 
                      value={editForm.currency_2}
                      options={settings?.currencies}
                      placeholder={t('auto.proj.selectCurrency2')}
                    />
                    <div />

                    <RestrictedInput field="hourly_rate_3" label={t('auto.proj.hourlyRate3')} type="number" value={editForm.hourly_rate_3} />
                    <RestrictedSelect 
                      field="currency_3" 
                      label={t('auto.proj.currency3')} 
                      value={editForm.currency_3}
                      options={settings?.currencies}
                      placeholder={t('auto.proj.selectCurrency2')}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Personal Info Sub-Tab */}
            <TabsContent value="personal" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>{t('auto.proj.personalInformation')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormDatePicker field="birth_date" label={t('auto.proj.birthDate')} value={editForm.birth_date} onDateChange={handleDateChange} />
                    <div>
                      <Label>{t('auto.proj.birthPlace')}</Label>
                      <Input value={editForm.birth_place || ''} onChange={(e) => handleInputChange('birth_place', e.target.value)} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.personalIdNumber')}</Label>
                      <Input value={editForm.personal_id_number || ''} onChange={(e) => handleInputChange('personal_id_number', e.target.value)} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.idCardNumber')}</Label>
                      <Input value={editForm.id_card_number || ''} onChange={(e) => handleInputChange('id_card_number', e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <FormDatePicker field="id_card_expiry_date" label={t('auto.proj.idCardExpiryDate')} value={editForm.id_card_expiry_date} onDateChange={handleDateChange} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.drivingLicenceNumber')}</Label>
                      <Input value={editForm.driving_licence_number || ''} onChange={(e) => handleInputChange('driving_licence_number', e.target.value)} />
                    </div>
                    <FormDatePicker field="driving_licence_expiry" label={t('auto.proj.drivingLicenceExpiry')} value={editForm.driving_licence_expiry} onDateChange={handleDateChange} />
                    <div>
                      <Label>{t('auto.proj.passportNumber')}</Label>
                      <Input value={editForm.passport_number || ''} onChange={(e) => handleInputChange('passport_number', e.target.value)} />
                    </div>
                    <FormDatePicker field="passport_issue_date" label={t('auto.proj.passportIssueDate')} value={editForm.passport_issue_date} onDateChange={handleDateChange} />
                    <FormDatePicker field="passport_expiry_date" label={t('auto.proj.passportExpiryDate')} value={editForm.passport_expiry_date} onDateChange={handleDateChange} />
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Contact - Full fields matching EmployeeForm */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('auto.proj.emergencyContact')}</CardTitle>
                  <CardDescription>{t('auto.proj.personToContactInCaseOf')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('auto.proj.surname')}</Label>
                      <Input 
                        value={editForm.emergency_contact?.surname || ''} 
                        onChange={(e) => handleEmergencyContactChange('surname', e.target.value)} 
                      />
                    </div>
                    <div>
                      <Label>{t('auto.proj.name')}</Label>
                      <Input 
                        value={editForm.emergency_contact?.name || ''} 
                        onChange={(e) => handleEmergencyContactChange('name', e.target.value)} 
                      />
                    </div>
                    <div>
                      <Label>{t('auto.proj.phone')}</Label>
                      <Input 
                        value={editForm.emergency_contact?.phone || ''} 
                        onChange={(e) => handleEmergencyContactChange('phone', e.target.value)} 
                      />
                    </div>
                    <div>
                      <Label>{t('auto.proj.email')}</Label>
                      <Input 
                        type="email"
                        value={editForm.emergency_contact?.email || ''} 
                        onChange={(e) => handleEmergencyContactChange('email', e.target.value)} 
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>{t('auto.proj.relationship')}</Label>
                      <Select 
                        value={editForm.emergency_contact?.relationship || ''} 
                        onValueChange={(value) => handleEmergencyContactChange('relationship', value)}
                      >
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectRelationship')} /></SelectTrigger>
                        <SelectContent>
                          {settings?.relationships?.map(rel => (
                            <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Business Info Sub-Tab */}
            <TabsContent value="business" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>{t('auto.proj.businessInformation')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('auto.proj.businessName')}</Label>
                      <Input value={editForm.business_name || ''} onChange={(e) => handleInputChange('business_name', e.target.value)} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.businessAddress')}</Label>
                      <Input value={editForm.business_address || ''} onChange={(e) => handleInputChange('business_address', e.target.value)} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.taxIdNumberNip')}</Label>
                      <Input value={editForm.tax_id_number || ''} onChange={(e) => handleInputChange('tax_id_number', e.target.value)} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.regonNumber')}</Label>
                      <Input value={editForm.regon_number || ''} onChange={(e) => handleInputChange('regon_number', e.target.value)} />
                    </div>
                    <div>
                      <Label>{t('auto.proj.pkdNumber')}</Label>
                      <Select value={editForm.pkd_number || ''} onValueChange={(value) => handleInputChange('pkd_number', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectPkdNumber')} /></SelectTrigger>
                        <SelectContent>
                          {settings?.pkd_numbers?.map(pkd => (
                            <SelectItem key={pkd} value={pkd}>{pkd}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('auto.proj.criminalRecord')}</Label>
                      <Select value={editForm.criminal_record || ''} onValueChange={(value) => handleInputChange('criminal_record', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectStatus')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="clean">{t('auto.proj.clean')}</SelectItem>
                          <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                          <SelectItem value="issues">{t('auto.proj.issues')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <FormDatePicker field="cr_cert_issue_date" label={t('auto.proj.crCertificateIssueDate')} value={editForm.cr_cert_issue_date} onDateChange={handleDateChange} />
                    <FormDatePicker field="contract_date" label={t('auto.proj.contractDate')} value={editForm.contract_date} disabled={true} onDateChange={handleDateChange} />
                    <RestrictedInput field="contract_number" label={t('auto.proj.contractNumber')} value={editForm.contract_number} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Certificates Sub-Tab */}
            <TabsContent value="certificates" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CertificateSection
                  title={t('auto.proj.electricalCertificates')}
                  category="electrical_certs"
                  certTypes={settings?.certificate_types?.electrical || []}
                  isCertificateChecked={isCertificateChecked}
                  getCertificateExpiryDate={getCertificateExpiryDate}
                  onToggle={handleCertificateToggle}
                  onExpiryChange={handleCertificateExpiryChange}
                />
                <CertificateSection
                  title={t('auto.proj.scissorLiftCertificates')}
                  category="scissor_lift_certs"
                  certTypes={settings?.certificate_types?.scissor_lift || []}
                  isCertificateChecked={isCertificateChecked}
                  getCertificateExpiryDate={getCertificateExpiryDate}
                  onToggle={handleCertificateToggle}
                  onExpiryChange={handleCertificateExpiryChange}
                />
                <CertificateSection
                  title={t('auto.proj.boomLiftCertificates')}
                  category="boom_lift_certs"
                  certTypes={settings?.certificate_types?.boom_lift || []}
                  isCertificateChecked={isCertificateChecked}
                  getCertificateExpiryDate={getCertificateExpiryDate}
                  onToggle={handleCertificateToggle}
                  onExpiryChange={handleCertificateExpiryChange}
                />
                <CertificateSection
                  title={t('auto.proj.oshaCertificates')}
                  category="osha_certs"
                  certTypes={settings?.certificate_types?.osha || []}
                  isCertificateChecked={isCertificateChecked}
                  getCertificateExpiryDate={getCertificateExpiryDate}
                  onToggle={handleCertificateToggle}
                  onExpiryChange={handleCertificateExpiryChange}
                />
                <CertificateSection
                  title={t('auto.proj.healthSafetyCertificates')}
                  category="health_safety_certs"
                  certTypes={settings?.certificate_types?.health_safety || []}
                  isCertificateChecked={isCertificateChecked}
                  getCertificateExpiryDate={getCertificateExpiryDate}
                  onToggle={handleCertificateToggle}
                  onExpiryChange={handleCertificateExpiryChange}
                />
              </div>

              {/* Legacy A1 Certificate */}
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>{t('auto.proj.a1CertificateLegacy')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('auto.proj.status')}</Label>
                      <Select value={editForm.a1_certificate || ''} onValueChange={(value) => handleInputChange('a1_certificate', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectStatus')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                          <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                          <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <FormDatePicker field="a1_expiry_date" label={t('auto.proj.a1ExpiryDate')} value={editForm.a1_expiry_date} onDateChange={handleDateChange} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Visa & Permits Sub-Tab */}
            <TabsContent value="visa" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>Visa &amp; Work Permits</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* EU Visa */}
                    <div>
                      <Label>{t('auto.proj.euVisaPermitStatus')}</Label>
                      <Select value={editForm.eu_visa || ''} onValueChange={(value) => handleInputChange('eu_visa', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectStatus')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                          <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                          <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('auto.proj.euVisaPermitType')}</Label>
                      <Select value={editForm.eu_visa_type || ''} onValueChange={(value) => handleInputChange('eu_visa_type', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectVisaPermitType')} /></SelectTrigger>
                        <SelectContent>
                          {settings?.eu_visa_types?.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormDatePicker field="eu_visa_expiry_date" label={t('auto.proj.euVisaPermitExpiryDate')} value={editForm.eu_visa_expiry_date} onDateChange={handleDateChange} />
                    <div />

                    {/* US Visa */}
                    <div>
                      <Label>{t('auto.proj.usVisaStatus')}</Label>
                      <Select value={editForm.us_visa || ''} onValueChange={(value) => handleInputChange('us_visa', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectStatus')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                          <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                          <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('auto.proj.usVisaType')}</Label>
                      <Select value={editForm.us_visa_type || ''} onValueChange={(value) => handleInputChange('us_visa_type', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectVisaType')} /></SelectTrigger>
                        <SelectContent>
                          {settings?.us_visa_types?.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormDatePicker field="us_visa_expiry_date" label={t('auto.proj.usVisaExpiryDate')} value={editForm.us_visa_expiry_date} onDateChange={handleDateChange} />
                    <div />

                    {/* UK Work Permit */}
                    <div>
                      <Label>{t('auto.proj.ukWorkPermitStatus')}</Label>
                      <Select value={editForm.uk_work_permit || ''} onValueChange={(value) => handleInputChange('uk_work_permit', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectStatus')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">{t('auto.proj.yes')}</SelectItem>
                          <SelectItem value="no">{t('auto.proj.no')}</SelectItem>
                          <SelectItem value="pending">{t('auto.proj.pending')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('auto.proj.ukWorkPermitType')}</Label>
                      <Select value={editForm.uk_work_permit_type || ''} onValueChange={(value) => handleInputChange('uk_work_permit_type', value)}>
                        <SelectTrigger><SelectValue placeholder={t('auto.proj.selectPermitType')} /></SelectTrigger>
                        <SelectContent>
                          {settings?.uk_work_permit_types?.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <FormDatePicker field="uk_work_permit_expiry_date" label={t('auto.proj.ukWorkPermitExpiryDate')} value={editForm.uk_work_permit_expiry_date} onDateChange={handleDateChange} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Avatar Tab */}
        <TabsContent value="avatar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('auto.proj.profilePhoto')}</CardTitle>
              <CardDescription>{t('auto.proj.uploadAProfilePhotoForYour')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <Avatar className="w-32 h-32">
                  {employee.avatar_file ? (
                    <AvatarImage src={`${BACKEND_URL}/api/employees/avatar/${employee.avatar_file}`} />
                  ) : null}
                  <AvatarFallback className="text-4xl bg-slate-200">
                    {(employee.name || employee.first_name || '?')[0]}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <FileUpload
                    type="avatar"
                    entityId={employee.id}
                    currentFile={employee.avatar_file}
                    onUploadComplete={handleAvatarUploadComplete}
                    onUploadError={() => toast.error(t('auto.proj.failedToUploadAvatar'))}
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    {t('auto.proj.recommendedSquareImageAtLeast200x200')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attachments Tab */}
        <TabsContent value="attachments" className="space-y-4">
          <AttachmentManager 
            employeeId={employee.id} 
            canDelete={isAdmin}
          />
        </TabsContent>

        {/* Security Tab - Change Password */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                {t('auto.proj.changePassword')}
              </CardTitle>
              <CardDescription>
                {t('auto.proj.updateYourAccountPasswordYouWill')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current-password">{t('auto.proj.currentPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="pr-10"
                      placeholder={t('auto.proj.enterYourCurrentPassword')}
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password">{t('auto.proj.newPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="pr-10"
                      placeholder={t('auto.proj.enterNewPassword')}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('auto.proj.minimum8Characters')}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('auto.proj.confirmNewPassword')}</Label>
                  <Input
                    id="confirm-password"
                    type={showNewPassword ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    placeholder={t('auto.proj.confirmNewPassword2')}
                    data-testid="confirm-password-input"
                  />
                  {confirmNewPassword && newPassword !== confirmNewPassword && (
                    <p className="text-xs text-red-500">{t('auto.proj.passwordsDoNotMatch')}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  disabled={
                    changingPassword || 
                    !currentPassword || 
                    newPassword.length < 8 || 
                    newPassword !== confirmNewPassword
                  }
                  data-testid="change-password-submit"
                >
                  {changingPassword ? t('auto.proj.changingPassword') : t('auto.proj.changePassword')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Submit Button (fixed at bottom when changes exist) */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('auto.proj.youHaveUnsavedChangesThatNeed')}
            </p>
            <Button onClick={handleSubmitChanges} disabled={submitting} data-testid="btn-submitchanges">
              {submitting ? t('auto.proj.submitting') : t('auto.proj.submitChangesForApproval')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;

// ── Default-signature settings card ──────────────────────────────
// Lets the user save a single "default signature" they can reuse
// with one tap on any sign-off screen. They can still redraw a
// fresh signature every time — this is purely opt-in convenience.
// Exported so it can be embedded in the AppHeader "Account" dialog
// as well as inside the full MyProfile tab.
export const MyDefaultSignatureCard = () => {
  const { t } = useTranslation();
  const { savedSignature, saveAsDefault, clearDefault, loading } = useDefaultSignature();
  const [draft, setDraft] = useState(null);

  useEffect(() => { setDraft(savedSignature); }, [savedSignature]);

  const hasDraft = !!draft && draft !== savedSignature;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auto.proj.myDefaultSignature')}</CardTitle>
        <CardDescription>
          Sign once here, then use the &quot;Use saved&quot; button on any checklist,
          test report or contract sign-off. You can still redraw a fresh signature
          each time — this is optional.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <div className="text-xs text-slate-400">{t('auto.proj.loading2')}</div>}

        {!loading && savedSignature && (
          <div className="p-2 border rounded bg-slate-50 flex items-center gap-3" data-testid="profile-saved-signature">
            <img src={savedSignature} alt={t('auto.proj.savedSignature')} className="max-h-20 bg-white border rounded p-1" />
            <div className="text-xs text-slate-500 flex-1">This signature is loaded whenever you tap &quot;Use saved&quot;.</div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm(t('auto.proj.removeYourDefaultSignature'))) return;
                const ok = await clearDefault();
                toast[ok ? 'success' : 'error'](ok ? t('auto.proj.defaultSignatureRemoved') : t('auto.proj.failedToClearSignature'));
              }}
              data-testid="profile-clear-signature-btn"
            >{t('auto.proj.remove')}</Button>
          </div>
        )}

        <div>
          <Label className="text-xs text-slate-500">
            {savedSignature ? t('auto.proj.updateYourDefaultSignature') : t('auto.proj.drawOrUploadYourSignature')}
          </Label>
          <SignaturePad
            value={draft}
            onChange={setDraft}
            height={140}
            testIdPrefix="profile-sig"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasDraft}
            onClick={() => setDraft(savedSignature)}
            data-testid="profile-signature-reset"
          >{t('auto.proj.reset')}</Button>
          <Button
            size="sm"
            disabled={!hasDraft || !draft}
            onClick={async () => {
              const ok = await saveAsDefault(draft);
              toast[ok ? 'success' : 'error'](ok ? t('auto.proj.defaultSignatureSaved') : t('auto.proj.failedToSave'));
            }}
            data-testid="profile-signature-save"
          >
            <Save className="h-3.5 w-3.5 mr-1" /> {t('auto.proj.saveAsDefault')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};



/**
 * MyLanguageCard — self-serve UI-language switcher.
 * Rendered only when the current user has the
 * `settings_language.change_own` permission (admin gets it by
 * default; every other role stays hidden unless the admin grants
 * it via the permissions matrix).
 */
export const MyLanguageCard = () => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { user } = useAuth();

  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState(user?.preferred_language || 'en');

  useEffect(() => {
    setLang(user?.preferred_language || 'en');
  }, [user?.preferred_language]);

  const apply = async (value) => {
    if (value === lang) return;
    const previous = lang;
    setSaving(true);
    setLang(value);
    try {
      await axios.put(`${API}/auth/me/language`, { language: value });
      i18n.changeLanguage(value);
      toast.success(t('profile.language.savedToast'));
    } catch (err) {
      setLang(previous);
      toast.error(err.response?.data?.detail || t('profile.language.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Gate the card — a user who cannot change their own language
  // should not see this control (hooks stay above the early return).
  if (!can('settings_language.change_own')) return null;

  return (
    <Card data-testid="my-language-card">
      <CardHeader>
        <CardTitle>{t('profile.language.sectionTitle')}</CardTitle>
        <CardDescription>{t('profile.language.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="max-w-xs space-y-2">
          <Label>{t('profile.language.current')}</Label>
          <Select value={lang} onValueChange={apply} disabled={saving}>
            <SelectTrigger data-testid="my-language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en" data-testid="my-language-option-en">
                {t('profile.language.english')}
              </SelectItem>
              <SelectItem value="pl" data-testid="my-language-option-pl">
                {t('profile.language.polish')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
};
